// frontend/packages/business-console/src/components/AiOverageOptIn.jsx
//
// Tenant "approve more AI usage" consent card. When the tenant is near /
// at the included monthly AI allowance, a businessadmin can opt in to keep
// going — additional usage is billed to the card on file in fixed
// increments. Opting in IS the consent; opting out stops future overage.
//
// Self-fetching. Renders NOTHING until the backend reports
// `available:true` (the feature is dark until the Phase 6 cutover), so it's
// safe to ship ahead of go-live. Opt-in/out is businessadmin-only
// server-side; non-admins get the server's 403 surfaced.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';

import {
  getOverageStatus,
  optInOverage,
  optOutOverage,
} from '../services/aiOverageService';

function money(n) {
  const v = Number(n) || 0;
  return `$${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}`;
}

export default function AiOverageOptIn() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const refresh = async () => {
    try {
      const s = await getOverageStatus();
      setStatus(s);
    } catch {
      setStatus(null); // non-fatal — card just hides
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Dark until the feature is live.
  if (loading || !status || !status.available) return null;

  const { optedIn, includedUsd, chargeIncrementUsd } = status;

  const doOptIn = async () => {
    setBusy(true);
    setError(null);
    try {
      await optInOverage();
      setConfirmOpen(false);
      await refresh();
    } catch (err) {
      const s = err?.response?.status;
      setError(
        s === 403
          ? 'Only business admins can turn this on.'
          : err?.response?.data?.message ||
              'Could not turn this on. Please try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  const doOptOut = async () => {
    setBusy(true);
    setError(null);
    try {
      await optOutOverage();
      await refresh();
    } catch (err) {
      const s = err?.response?.status;
      setError(
        s === 403
          ? 'Only business admins can change this.'
          : err?.response?.data?.message ||
              'Could not update this. Please try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: optedIn ? 'success.light' : 'divider',
        borderRadius: 2,
        p: 2.5,
        mt: 3,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <BoltRoundedIcon
          fontSize="small"
          sx={{ color: optedIn ? 'success.main' : 'text.secondary' }}
        />
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Keep building after your included AI
        </Typography>
        {optedIn ? (
          <Chip size="small" color="success" label="On" />
        ) : null}
      </Stack>

      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Every month includes {money(includedUsd)} of AI at no charge. Turn
        this on to keep using AI features past that — additional usage is
        billed to your card on file in {money(chargeIncrementUsd)} increments.
        You can turn it off anytime.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {optedIn ? (
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          disabled={busy}
          onClick={doOptOut}
          startIcon={busy ? <CircularProgress size={14} /> : null}
        >
          Turn off
        </Button>
      ) : (
        <Button
          variant="contained"
          size="small"
          disabled={busy}
          onClick={() => setConfirmOpen(true)}
        >
          Turn on
        </Button>
      )}

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)}>
        <DialogTitle>Turn on AI overage?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            After your {money(includedUsd)} of included AI each month, further
            AI usage will be charged to your card on file in{' '}
            {money(chargeIncrementUsd)} increments. You can turn this off
            anytime in this page.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={doOptIn}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : null}
          >
            Turn on
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
